import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { APP_CONFIG, ROUTES, SOCIAL_LINKS, SEO } from '../../constants'

type SchemaValue = Record<string, unknown>

interface MetaConfig {
  title: string
  description: string
  path: string
  keywords?: string[]
  imagePath?: string
  robots?: string
  type?: 'website' | 'article' | 'profile'
  schemaType?: 'WebPage' | 'CollectionPage' | 'ProfilePage' | 'AboutPage' | 'SoftwareApplication'
}

const DEFAULT_SITE_URL = 'https://ertugrulkucukkaya.com'
const DEFAULT_IMAGE_PATH = '/project/iPortfolio.png'
const DEFAULT_LOCALE = 'en_US'

const trimTrailingSlash = (value: string) => value.replace(/\/$/, '')

const toAbsoluteUrl = (siteUrl: string, path: string) => new URL(path, `${siteUrl}/`).toString()

const createOrUpdateMetaTag = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute('data-seo-managed', 'true')
    document.head.appendChild(element)
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element?.setAttribute(name, value)
  })
}

const createOrUpdateLinkTag = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('data-seo-managed', 'true')
    document.head.appendChild(element)
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element?.setAttribute(name, value)
  })
}

const resolveSiteUrl = () => {
  const configuredUrl = import.meta.env.VITE_APP_URL

  if (configuredUrl && !configuredUrl.includes('your-domain.vercel.app')) {
    return trimTrailingSlash(configuredUrl)
  }

  if (typeof window === 'undefined') {
    return DEFAULT_SITE_URL
  }

  return trimTrailingSlash(window.location.origin || DEFAULT_SITE_URL)
}

const getMainSiteMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    [ROUTES.HOME]: {
      title: 'Ertugrul Kucukkaya | Systems, Writing, and Products',
      description: 'Personal website of Ertugrul Kucukkaya featuring product experiments, writing, reading notes, and long-horizon projects.',
      path: ROUTES.HOME,
      keywords: [
        ...SEO.DEFAULT_KEYWORDS,
        'Ertugrul Kucukkaya',
        'software engineer',
        'product builder',
        'writing',
        'reading notes',
      ],
      type: 'profile',
      schemaType: 'ProfilePage',
    },
    [ROUTES.BLOG]: {
      title: 'Blog | Essays, Notes, and Audio Posts',
      description: 'Read essays, notes, and audio-supported posts on focus, systems, work, and long-term thinking.',
      path: ROUTES.BLOG,
      keywords: ['blog essays', 'audio blog', 'deep work notes', 'technology writing'],
      type: 'website',
      schemaType: 'CollectionPage',
    },
    [ROUTES.BOOKS]: {
      title: 'Books | Reading Journal and Recommendations',
      description: 'Explore current reads, completed books, ratings, and book notes from an evolving personal reading journal.',
      path: ROUTES.BOOKS,
      keywords: ['reading journal', 'book recommendations', 'bookshelf', 'book notes'],
      type: 'website',
      schemaType: 'CollectionPage',
    },
    [ROUTES.PROJECTS]: {
      title: 'Projects',
      description: 'Browse shipped projects, active experiments, and the decisions behind them, including iPortfolio and related product work.',
      path: ROUTES.PROJECTS,
      keywords: ['projects portfolio', 'product experiments', 'software projects', 'iPortfolio'],
      type: 'website',
      schemaType: 'CollectionPage',
      imagePath: '/project/iPortfolio.png',
    },
    [ROUTES.LEGACY]: {
      title: 'Legacy | Archived Ideas and Long-Form Reflections',
      description: 'An archive of long-form reflections, legacy notes, and ideas intended to outlast the current moment.',
      path: ROUTES.LEGACY,
      keywords: ['legacy archive', 'philosophy notes', 'long-form reflections'],
      type: 'website',
      schemaType: 'CollectionPage',
    },
    '/admin': {
      title: 'Admin | Content Management',
      description: 'Administrative content management surface for the website.',
      path: '/admin',
      robots: 'noindex, nofollow, noarchive',
      type: 'website',
      schemaType: 'WebPage',
    },
    '/test-db': {
      title: 'Database Test | Internal Utility',
      description: 'Internal database diagnostic utility.',
      path: '/test-db',
      robots: 'noindex, nofollow, noarchive',
      type: 'website',
      schemaType: 'WebPage',
    },
  }

  if (pathname.startsWith('/admin')) {
    return pageMap['/admin']
  }

  return pageMap[pathname] || pageMap[ROUTES.HOME]
}

const getIPortfolioMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    '/home': {
      title: 'Intelligent Portfolio | AI-Powered Portfolio Companion',
      description: 'Intelligent Portfolio is an AI-powered portfolio companion focused on insight, organization, and decision support.',
      path: '/home',
      keywords: ['Intelligent Portfolio', 'portfolio app', 'AI portfolio', 'investment companion'],
      type: 'website',
      schemaType: 'SoftwareApplication',
      imagePath: '/project/iPortfolio.png',
    },
    '/privacy': {
      title: 'Intelligent Portfolio Privacy Policy',
      description: 'Privacy policy for Intelligent Portfolio, covering data handling, usage, and user protections.',
      path: '/privacy',
      keywords: ['Intelligent Portfolio privacy'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/project/iPortfolio.png',
    },
    '/terms': {
      title: 'Intelligent Portfolio Terms of Service',
      description: 'Terms of service for Intelligent Portfolio, including usage terms and service boundaries.',
      path: '/terms',
      keywords: ['Intelligent Portfolio terms'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/project/iPortfolio.png',
    },
  }

  return pageMap[pathname] || pageMap['/home']
}

const getDicteraMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    '/home': {
      title: 'Dictera — Private, on-device voice dictation',
      description: 'Dictera records your voice and transcribes it to text in real time, entirely on your device. No accounts, no servers — your words stay private.',
      path: '/home',
      keywords: ['Dictera', 'dictation app', 'voice to text', 'transcription', 'on-device', 'privacy'],
      type: 'website',
      schemaType: 'SoftwareApplication',
      imagePath: '/dictera/og.png',
    },
    '/privacy': {
      title: 'Privacy Policy — Dictera',
      description: 'How Dictera handles your data: account-free, on-device transcription, optional encrypted iCloud / Google Drive backup, and nothing sent to our servers.',
      path: '/privacy',
      keywords: ['Dictera privacy', 'on-device', 'privacy policy'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/dictera/og.png',
    },
    '/terms': {
      title: 'Terms of Use — Dictera',
      description: 'Terms of Use for Dictera, including subscription plans, the 14-day free trial, billing through Apple, and acceptable use.',
      path: '/terms',
      keywords: ['Dictera terms', 'terms of use'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/dictera/og.png',
    },
    '/support': {
      title: 'Support — Dictera',
      description: 'Get help with Dictera: contact, FAQs, managing your subscription, restoring purchases, and troubleshooting.',
      path: '/support',
      keywords: ['Dictera support', 'help', 'contact'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/dictera/og.png',
    },
  }

  return pageMap[pathname] || pageMap['/home']
}

const getPomonMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    '/home': {
      title: 'Pomon | 15-Minute Focus Timer',
      description: 'Pomon is a minimalist focus timer: set one goal, run a 15-minute sprint, celebrate the win, and watch your focus add up. Free, private, and offline-first.',
      path: '/home',
      keywords: ['Pomon', 'focus timer', '15 minute timer', 'pomodoro alternative', 'deep work', 'productivity'],
      type: 'website',
      schemaType: 'SoftwareApplication',
      imagePath: '/pomon/appstore.png',
    },
    '/privacy': {
      title: 'Pomon Privacy Policy',
      description: "Pomon's privacy policy: local-first, no tracking or ads, with optional Sign in with Apple and private iCloud sync.",
      path: '/privacy',
      keywords: ['Pomon privacy'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/pomon/appstore.png',
    },
    '/terms': {
      title: 'Pomon Terms of Use',
      description: 'Terms of use for Pomon, including license, optional account, and governing law.',
      path: '/terms',
      keywords: ['Pomon terms'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/pomon/appstore.png',
    },
  }

  return pageMap[pathname] || pageMap['/home']
}

const getRobotsMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    '/home': {
      title: 'Robot Factory — Idle Assembly Tycoon',
      description: 'Robot Factory is an idle assembly tycoon: build a chain of single-purpose robots, automate the line, prestige for permanent upgrades, and launch a rocket to Mars. Free, with no ads, no accounts, and no tracking.',
      path: '/home',
      keywords: ['Robot Factory', 'idle game', 'incremental game', 'tycoon', 'factory game', 'automation', 'Mars', 'prestige', 'iOS game'],
      type: 'website',
      schemaType: 'SoftwareApplication',
      imagePath: '/robots/og.png',
    },
    '/privacy': {
      title: 'Privacy Policy — Robot Factory',
      description: 'How Robot Factory handles your data: account-free, on-device save with optional private iCloud backup, StoreKit purchases, and no tracking, ads, or analytics.',
      path: '/privacy',
      keywords: ['Robot Factory privacy', 'privacy policy', 'no tracking'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/robots/og.png',
    },
    '/terms': {
      title: 'Terms of Use — Robot Factory',
      description: 'Terms of Use for Robot Factory, including in-app purchases, virtual items and currency, billing through Apple, and acceptable use.',
      path: '/terms',
      keywords: ['Robot Factory terms', 'terms of use'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/robots/og.png',
    },
    '/support': {
      title: 'Support — Robot Factory',
      description: 'Get help with Robot Factory: contact, FAQs, restoring purchases, iCloud backup, and starting over.',
      path: '/support',
      keywords: ['Robot Factory support', 'help', 'contact'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/robots/og.png',
    },
  }

  return pageMap[pathname] || pageMap['/home']
}

const getMyemMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    '/home': {
      title: 'Myem — Your own music, offline',
      description: 'Myem turns the audio files you already own into a clean, offline music library for iPhone — no streaming, no account, no ads.',
      path: '/home',
      keywords: ['Myem', 'offline music player', 'local music', 'mp3 player', 'music library', 'iPhone', 'offline'],
      type: 'website',
      schemaType: 'SoftwareApplication',
      imagePath: '/myem/appstore.png',
    },
    '/privacy': {
      title: 'Privacy Policy — Myem',
      description: "Myem's privacy policy: no account, no ads, no analytics, no tracking. Your music and library stay on your device.",
      path: '/privacy',
      keywords: ['Myem privacy', 'privacy policy', 'no tracking'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/myem/appstore.png',
    },
    '/terms': {
      title: 'Terms of Use — Myem',
      description: 'Terms of Use for Myem, including license, your content, and governing law.',
      path: '/terms',
      keywords: ['Myem terms', 'terms of use'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/myem/appstore.png',
    },
  }

  return pageMap[pathname] || pageMap['/home']
}

const getCoinTaxMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    '/home': {
      title: 'Krypto-Steuer — Wann wird dein Krypto-Gewinn steuerfrei?',
      description: 'Krypto-Steuer zeigt dir für jede Position, wann sie nach §23 EStG steuerfrei wird — mit Haltefrist-Countdown, Freigrenze-Check und Finanzamt-Report. Alles auf deinem Gerät.',
      path: '/home',
      keywords: ['Krypto-Steuer', 'Krypto Steuer', '§23 EStG', 'Haltefrist', 'Freigrenze', 'FIFO', 'LIFO', 'Finanzamt', 'Bitcoin Steuer', 'Anlage SO'],
      type: 'website',
      schemaType: 'SoftwareApplication',
      imagePath: '/cointax/appstore.png',
    },
    '/privacy': {
      title: 'Datenschutzerklärung — Krypto-Steuer',
      description: 'Datenschutz bei Krypto-Steuer: kein Konto, keine Cloud, kein Tracking. Deine Transaktionen und Wallets bleiben verschlüsselt auf deinem Gerät.',
      path: '/privacy',
      keywords: ['Krypto-Steuer Datenschutz', 'Datenschutzerklärung', 'kein Tracking'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/cointax/appstore.png',
    },
    '/terms': {
      title: 'Nutzungsbedingungen — Krypto-Steuer',
      description: 'Nutzungsbedingungen für Krypto-Steuer: einmaliger Kauf, Lizenz, keine Steuerberatung und anwendbares Recht.',
      path: '/terms',
      keywords: ['Krypto-Steuer Nutzungsbedingungen', 'Nutzungsbedingungen', 'AGB'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/cointax/appstore.png',
    },
  }

  return pageMap[pathname] || pageMap['/home']
}

const getIklapMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    '/home': {
      title: 'iklap — A quiet place to read',
      description: 'iklap is a calm, private reader for EPUB, PDF, Markdown, and Word: import the books and documents you own and read them beautifully, with highlights, notes, and bookmarks that stay on your device. No accounts, no servers.',
      path: '/home',
      keywords: ['iklap', 'EPUB reader', 'PDF reader', 'document reader', 'ebook reader', 'reading app', 'highlights', 'private reader', 'offline reading'],
      type: 'website',
      schemaType: 'SoftwareApplication',
    },
    '/privacy': {
      title: 'Privacy Policy — iklap',
      description: 'How iklap handles your data: account-free, on-device reading, with your books, highlights, and notes never leaving your device. No tracking, no ads, no analytics.',
      path: '/privacy',
      keywords: ['iklap privacy', 'privacy policy', 'on-device', 'no tracking'],
      type: 'website',
      schemaType: 'WebPage',
    },
    '/terms': {
      title: 'Terms of Use — iklap',
      description: 'Terms of Use for iklap: the free library that holds three books at a time, the single one-time purchase that removes the limit, billing through Apple and Google, and acceptable use.',
      path: '/terms',
      keywords: ['iklap terms', 'terms of use'],
      type: 'website',
      schemaType: 'WebPage',
    },
  }

  return pageMap[pathname] || pageMap['/home']
}

const getPoesieMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    '/home': {
      title: 'Poesie — Şiir Oku, Şiir Defteri',
      description: 'Poesie, cebinizde sessiz bir şiir kitaplığı: 134 şair, 12 dil, 9.930 şiir. Kendi şiirlerinizi yazın; hepsi internetsiz, reklamsız ve hesapsız.',
      path: '/home',
      keywords: ['Poesie', 'şiir', 'şiir oku', 'şiir defteri', 'şiirler', 'şairler', 'Türk şiiri', 'aşk şiirleri', 'Yunus Emre', 'Orhan Veli', 'Karacaoğlan', 'poetry app', 'iPhone', 'iPad'],
      type: 'website',
      schemaType: 'SoftwareApplication',
      imagePath: '/poesie/og.png',
    },
    '/privacy-policy': {
      title: 'Gizlilik Politikası — Poesie',
      description: 'Poesie hiçbir kişisel veri toplamaz: sunucu, hesap, reklam, analiz ya da takip yok. Favorileriniz ve şiirleriniz cihazınızda kalır.',
      path: '/privacy-policy',
      keywords: ['Poesie gizlilik', 'gizlilik politikası', 'privacy policy'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/poesie/og.png',
    },
    '/terms': {
      title: 'Kullanım Koşulları — Poesie',
      description: 'Poesie kullanım koşulları: lisans, şiirler ve telif, kendi şiirleriniz ve uygulanacak hukuk.',
      path: '/terms',
      keywords: ['Poesie kullanım koşulları', 'terms of use'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/poesie/og.png',
    },
    '/support': {
      title: 'Destek — Poesie',
      description: 'Poesie için sık sorulanlar ve iletişim: kısaltılmış şiirler, kendi şiirleriniz, favoriler, müzik ve daha fazlası.',
      path: '/support',
      keywords: ['Poesie destek', 'support'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/poesie/og.png',
    },
    '/copyright': {
      title: 'Telif ve Kaldırma — Poesie',
      description: 'Poesie hangi şiiri nasıl gösterir: kamu malı eserler tam metin, telif süresi dolmamış eserler kısa alıntı. Hak sahipleri için iletişim.',
      path: '/copyright',
      keywords: ['Poesie telif', 'telif hakkı', 'copyright', 'takedown'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/poesie/og.png',
    },
  }

  return pageMap[pathname] || pageMap['/home']
}

const isPoesieHost = (hostname: string) =>
  hostname === 'poesie.ertugrulkucukkaya.com' || hostname.startsWith('poesie.')

const getNamaaMeta = (pathname: string): MetaConfig => {
  const pageMap: Record<string, MetaConfig> = {
    '/home': {
      title: 'Namaa — Learn Arabic, one word a day',
      description: 'Namaa teaches you to write the Arabic alphabet by hand, then a new word every day to hear in a real sentence, say out loud and write from memory. For iPhone and iPad.',
      path: '/home',
      keywords: ['Namaa', 'learn Arabic', 'Arabic alphabet', 'Arabic handwriting', 'Arabic vocabulary', 'iPhone', 'iPad'],
      type: 'website',
      schemaType: 'SoftwareApplication',
      imagePath: '/namaa/icon.png',
    },
    '/privacy': {
      title: 'Privacy Policy — Namaa',
      description: "Namaa's privacy policy: no account, no ads, no analytics, no tracking. Your progress stays on your device.",
      path: '/privacy',
      keywords: ['Namaa privacy', 'privacy policy', 'no tracking'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/namaa/icon.png',
    },
    '/support': {
      title: 'Support — Namaa',
      description: 'Help with Namaa: the placement quiz, tracing, pronunciation, Arabic voices and widgets, and how to get in touch.',
      path: '/support',
      keywords: ['Namaa support', 'help', 'contact'],
      type: 'website',
      schemaType: 'WebPage',
      imagePath: '/namaa/icon.png',
    },
  }

  return pageMap[pathname] || pageMap['/home']
}

const isPomonHost = (hostname: string) =>
  hostname === 'pomon.ertugrulkucukkaya.com' || hostname.startsWith('pomon.') ||
  hostname === 'focus.ertugrulkucukkaya.com' || hostname.startsWith('focus.')

const isRobotsHost = (hostname: string) =>
  hostname === 'robots.ertugrulkucukkaya.com' || hostname.startsWith('robots.')

const isMyemHost = (hostname: string) =>
  hostname === 'myem.ertugrulkucukkaya.com' || hostname.startsWith('myem.')

const isNamaaHost = (hostname: string) =>
  hostname === 'namaa.ertugrulkucukkaya.com' || hostname.startsWith('namaa.')

const isCoinTaxHost = (hostname: string) =>
  hostname === 'cointax.ertugrulkucukkaya.com' || hostname.startsWith('cointax.')

const isIklapHost = (hostname: string) =>
  hostname === 'iklap.ertugrulkucukkaya.com' || hostname.startsWith('iklap.')

const getMetaConfig = (hostname: string, pathname: string) => {
  if (hostname === 'iportfolio.ertugrulkucukkaya.com' || hostname.startsWith('iportfolio.')) {
    return getIPortfolioMeta(pathname)
  }

  if (hostname === 'dictera.ertugrulkucukkaya.com' || hostname.startsWith('dictera.')) {
    return getDicteraMeta(pathname)
  }

  if (isPomonHost(hostname)) {
    return getPomonMeta(pathname)
  }

  if (isRobotsHost(hostname)) {
    return getRobotsMeta(pathname)
  }

  if (isMyemHost(hostname)) {
    return getMyemMeta(pathname)
  }

  if (isNamaaHost(hostname)) {
    return getNamaaMeta(pathname)
  }

  if (isCoinTaxHost(hostname)) {
    return getCoinTaxMeta(pathname)
  }

  if (isIklapHost(hostname)) {
    return getIklapMeta(pathname)
  }

  if (isPoesieHost(hostname)) {
    return getPoesieMeta(pathname)
  }

  return getMainSiteMeta(pathname)
}

const buildSchemas = (meta: MetaConfig, siteUrl: string, canonicalUrl: string, imageUrl: string, hostname: string): SchemaValue[] => {
  const personName = APP_CONFIG.AUTHOR.replace('Kücükkaya', 'Kucukkaya')
  const sameAs = [SOCIAL_LINKS.GITHUB, SOCIAL_LINKS.LINKEDIN].filter(Boolean)

  if (hostname === 'iportfolio.ertugrulkucukkaya.com' || hostname.startsWith('iportfolio.')) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Intelligent Portfolio',
        operatingSystem: 'iOS',
        applicationCategory: 'FinanceApplication',
        description: meta.description,
        url: canonicalUrl,
        image: imageUrl,
        author: {
          '@type': 'Person',
          name: personName,
          url: DEFAULT_SITE_URL,
        },
      },
    ]
  }

  if (hostname === 'dictera.ertugrulkucukkaya.com' || hostname.startsWith('dictera.')) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Dictera',
        operatingSystem: 'iOS, iPadOS, macOS, Android',
        applicationCategory: 'ProductivityApplication',
        description: meta.description,
        url: canonicalUrl,
        image: imageUrl,
        author: {
          '@type': 'Person',
          name: personName,
          url: DEFAULT_SITE_URL,
        },
      },
    ]
  }

  if (isPomonHost(hostname)) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Pomon',
        operatingSystem: 'iOS, Android',
        applicationCategory: 'ProductivityApplication',
        description: meta.description,
        url: canonicalUrl,
        image: imageUrl,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        author: {
          '@type': 'Person',
          name: personName,
          url: DEFAULT_SITE_URL,
        },
      },
    ]
  }

  if (isRobotsHost(hostname)) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Robot Factory',
        operatingSystem: 'iOS',
        applicationCategory: 'GameApplication',
        description: meta.description,
        url: canonicalUrl,
        image: imageUrl,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        author: {
          '@type': 'Person',
          name: personName,
          url: DEFAULT_SITE_URL,
        },
      },
    ]
  }

  if (isMyemHost(hostname)) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Myem',
        operatingSystem: 'iOS',
        applicationCategory: 'MultimediaApplication',
        description: meta.description,
        url: canonicalUrl,
        image: imageUrl,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        author: {
          '@type': 'Person',
          name: personName,
          url: DEFAULT_SITE_URL,
        },
      },
    ]
  }

  if (isCoinTaxHost(hostname)) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Krypto-Steuer',
        operatingSystem: 'iOS',
        applicationCategory: 'FinanceApplication',
        description: meta.description,
        url: canonicalUrl,
        image: imageUrl,
        inLanguage: 'de',
        offers: {
          '@type': 'Offer',
          price: '4.99',
          priceCurrency: 'EUR',
        },
        author: {
          '@type': 'Person',
          name: personName,
          url: DEFAULT_SITE_URL,
        },
      },
    ]
  }

  if (isIklapHost(hostname)) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'iklap',
        operatingSystem: 'iOS, Android',
        applicationCategory: 'BookApplication',
        description: meta.description,
        url: canonicalUrl,
        image: imageUrl,
        // iklap is free to download. The $14.99 unlock is an in-app purchase,
        // not the price of the app, so the Offer must read 0 — quoting the
        // unlock here would contradict the store listing everywhere this
        // schema is scraped.
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        author: {
          '@type': 'Person',
          name: personName,
          url: DEFAULT_SITE_URL,
        },
      },
    ]
  }

  if (isPoesieHost(hostname)) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Poesie',
        operatingSystem: 'iOS, iPadOS',
        applicationCategory: 'BookApplication',
        description: meta.description,
        url: canonicalUrl,
        image: imageUrl,
        inLanguage: 'tr',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        author: {
          '@type': 'Person',
          name: personName,
          url: DEFAULT_SITE_URL,
        },
      },
    ]
  }

  if (isNamaaHost(hostname)) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Namaa',
        operatingSystem: 'iOS, iPadOS',
        applicationCategory: 'EducationalApplication',
        description: meta.description,
        url: canonicalUrl,
        image: imageUrl,
        author: {
          '@type': 'Person',
          name: personName,
          url: DEFAULT_SITE_URL,
        },
      },
    ]
  }

  const basePageSchema: SchemaValue = {
    '@context': 'https://schema.org',
    '@type': meta.schemaType || 'WebPage',
    name: meta.title,
    description: meta.description,
    url: canonicalUrl,
    inLanguage: 'en',
    primaryImageOfPage: imageUrl,
  }

  if (meta.path === ROUTES.HOME) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: personName,
        url: siteUrl,
        image: imageUrl,
        description: meta.description,
        sameAs,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: personName,
        url: siteUrl,
        description: meta.description,
        inLanguage: 'en',
        publisher: {
          '@type': 'Person',
          name: personName,
        },
      },
      basePageSchema,
    ]
  }

  return [
    basePageSchema,
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: meta.title,
          item: canonicalUrl,
        },
      ],
    },
  ]
}

const SeoHead = () => {
  const location = useLocation()

  useEffect(() => {
    const hostname = typeof window === 'undefined' ? 'ertugrulkucukkaya.com' : window.location.hostname
    const siteUrl = resolveSiteUrl()
    const meta = getMetaConfig(hostname, location.pathname)
    const title = `${meta.title}${meta.path === ROUTES.HOME ? '' : `${SEO.TITLE_SEPARATOR}${APP_CONFIG.AUTHOR.replace('Kücükkaya', 'Kucukkaya')}`}`
    const canonicalUrl = toAbsoluteUrl(siteUrl, meta.path)
    const imageUrl = toAbsoluteUrl(siteUrl, meta.imagePath || DEFAULT_IMAGE_PATH)
    const keywords = [...SEO.DEFAULT_KEYWORDS, ...(meta.keywords || [])]
    const robots = meta.robots || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    const schemas = buildSchemas(meta, siteUrl, canonicalUrl, imageUrl, hostname)

    document.title = title
    document.documentElement.lang = 'en'

    createOrUpdateMetaTag('meta[name="description"]', { name: 'description', content: meta.description })
    createOrUpdateMetaTag('meta[name="keywords"]', { name: 'keywords', content: keywords.join(', ') })
    createOrUpdateMetaTag('meta[name="author"]', { name: 'author', content: APP_CONFIG.AUTHOR })
    createOrUpdateMetaTag('meta[name="robots"]', { name: 'robots', content: robots })
    createOrUpdateMetaTag('meta[name="googlebot"]', { name: 'googlebot', content: robots })
    createOrUpdateMetaTag('meta[property="og:type"]', { property: 'og:type', content: meta.type || 'website' })
    createOrUpdateMetaTag('meta[property="og:title"]', { property: 'og:title', content: title })
    createOrUpdateMetaTag('meta[property="og:description"]', { property: 'og:description', content: meta.description })
    createOrUpdateMetaTag('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
    createOrUpdateMetaTag('meta[property="og:site_name"]', { property: 'og:site_name', content: APP_CONFIG.AUTHOR })
    createOrUpdateMetaTag('meta[property="og:locale"]', { property: 'og:locale', content: DEFAULT_LOCALE })
    createOrUpdateMetaTag('meta[property="og:image"]', { property: 'og:image', content: imageUrl })
    createOrUpdateMetaTag('meta[property="og:image:alt"]', { property: 'og:image:alt', content: meta.title })
    createOrUpdateMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
    createOrUpdateMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
    createOrUpdateMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: meta.description })
    createOrUpdateMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl })
    createOrUpdateMetaTag('meta[name="theme-color"]', { name: 'theme-color', content: '#000000' })
    createOrUpdateLinkTag('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl })

    let structuredData = document.head.querySelector('script[data-seo-managed="structured-data"]') as HTMLScriptElement | null

    if (!structuredData) {
      structuredData = document.createElement('script')
      structuredData.type = 'application/ld+json'
      structuredData.setAttribute('data-seo-managed', 'structured-data')
      document.head.appendChild(structuredData)
    }

    structuredData.textContent = JSON.stringify(schemas)
  }, [location.pathname])

  return null
}

export default SeoHead